import React, { useCallback, useContext, useMemo, useState } from 'react';
import ModalDialog, { ModalTransition } from '@atlaskit/modal-dialog';
import Form from '../../../../../kingpin/forms/Form';
import FormHeader from '../../../../../kingpin/forms/FormHeader';
import Button from '../../../../../kingpin/atoms/Button';
import Row from '../../../../../components/Common/Row';
import FormContent from '../../../../../kingpin/forms/FormContent';
import MetricsContext from '../../../../../contexts/MetricsContext';
import DuplicateMetricSelector from './DuplicateMetricSelector';
import isDefined from '../../../../../isDefined';
import PopupStyles from '../../../../../components/Inputs/MetricPicker/MetricPickerPopup/PopupStyles';
import Typography from '../../../../../kingpin/atoms/Typography';
import {
  MergeDuplicateMetricsContext,
  MergeDuplicateMetricsProvider,
} from './MergeDuplicateMetrics';
import { Scrollable } from '../../../../../components/UserPicker/UserPicker';
import DuplicateMetricUsage from './DuplicateMetricUsage';
import useMergeDuplicateMetrics from './useMergeDuplicateMetrics';
import ToastContext from '../../../../../contexts/ToastContext';
import Loading from '../../../../../components/Loading';
import captureException from '../../../../../services/captureException';

const MergeDuplicateMetrics = ({ close }: { close: () => void }) => {
  const [step, setStep] = useState<'1' | '2'>('1');
  const [isLoading, setIsLoading] = useState(false);
  const { allMetrics } = useContext(MetricsContext);
  const { primaryMetric, duplicateMetrics } = useContext(
    MergeDuplicateMetricsContext,
  );
  const mergeDuplicateMetrics = useMergeDuplicateMetrics();

  const { showToast } = useContext(ToastContext);

  const mergeMetrics = useCallback(() => {
    setIsLoading(true);
    mergeDuplicateMetrics()
      .then(async () => {
        setIsLoading(false);
        showToast('Duplicate metrics merged');
        close();
      })
      .catch((e) => {
        captureException(e);
        setIsLoading(false);
        showToast(e.message);
      });
  }, [close, mergeDuplicateMetrics, showToast]);

  const DuplicateOptions = useMemo(() => {
    return allMetrics
      .map((metric) => (
        <DuplicateMetricSelector key={metric.id} metric={metric} />
      ))
      .filter(isDefined);
  }, [allMetrics]);

  const Body = useMemo(() => {
    if (step === '1') {
      return (
        <>
          {!duplicateMetrics.length && !isLoading && (
            <Typography.Header textAlign={'center'} type={'H3'}>
              No Duplicate Metrics found.
            </Typography.Header>
          )}
          {isLoading && <Loading />}
          {!isLoading && (
            <>
              {duplicateMetrics.length > 0 && (
                <Typography.Header type={'H4'}>
                  Select a metric with duplicates
                </Typography.Header>
              )}
              <Scrollable
                style={{
                  maxHeight: 500,
                  marginTop: 5,
                  padding: 4,
                }}
              >
                <PopupStyles.PopupContent.ListInnerDiv>
                  {DuplicateOptions}
                </PopupStyles.PopupContent.ListInnerDiv>
              </Scrollable>
            </>
          )}
        </>
      );
    } else {
      return <DuplicateMetricUsage />;
    }
  }, [DuplicateOptions, duplicateMetrics.length, isLoading, step]);

  return (
    <>
      <Form>
        <FormHeader title={'Merge Duplicate Metrics'} />
        <FormContent>{Body}</FormContent>
        <Row spaceBetween>
          {step === '1' && (
            <>
              <Button
                onClick={close}
                type="Secondary"
                size="Small"
                label="Close"
              />
              <Button
                type="Primary"
                size="Small"
                onClick={() => setStep('2')}
                isDisabled={!primaryMetric}
                label="Next"
              />
            </>
          )}
          {step === '2' && (
            <>
              <Button
                onClick={() => setStep('1')}
                type="Secondary"
                size="Small"
                label="Back"
              />
              <Button
                type="Primary"
                testId={'merge-duplicate-metrics-button'}
                onClick={mergeMetrics}
                isDisabled={!primaryMetric}
                size="Small"
                label="Merge"
              />
            </>
          )}
        </Row>
      </Form>
    </>
  );
};

const Gate = ({ isOpen, close }: { isOpen: boolean; close: () => void }) => (
  <ModalTransition>
    {isOpen && (
      <ModalDialog
        width={450}
        onClose={close}
        shouldScrollInViewport={false}
        autoFocus={false}
      >
        <MergeDuplicateMetricsProvider>
          <MergeDuplicateMetrics close={close} />
        </MergeDuplicateMetricsProvider>
      </ModalDialog>
    )}
  </ModalTransition>
);

export default Gate;
